// src/shared/ui/components/page-header.tsx
// Zone contract Z4 (ContextHeader, app pages): the page header at the top of the workspace frame.
// One row, context left, actions right (design log 13 Oct, Rule "the header settled: small title,
// more air", the last of the header passes):
//   - 80px tall, 16px padding on all four sides, the hairline drawn INSIDE (it never adds a pixel);
//   - context: an optional 48px photo (`leading`), then the title 24/32/600 in primary ink over one
//     subline 13/20 in secondary ink, the subline's bottom 16 above the hairline;
//   - right: underline tabs (`tabs`) as tall as the header, their labels on the subline's bottom
//     line and the active underline lying on the hairline; then the actions, every control 32 tall,
//     16 from the bottom and the right edge.
// Looks live in src/styles/showcase-bridge.css under `/* shell */` (the PA forbids arbitrary
// Tailwind values). Graduation candidate: a PageHeader frame in Signal DS.
import type { ReactNode } from 'react';

interface PageHeaderProps {
  title: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  tabs?: ReactNode;
  /** A 48px photo or mark left of the title (a patient's photo). */
  leading?: ReactNode;
  /** A quiet tag right after the title, on its line (e.g. "Merged"). */
  tag?: ReactNode;
}

export function PageHeader({ title, meta, actions, tabs, leading, tag }: PageHeaderProps) {
  return (
    <header data-slot="page-header" className="page-header">
      {leading != null && (
        <div data-slot="page-header-leading" className="page-header-leading">
          {leading}
        </div>
      )}
      <div className="page-header-context">
        {tag != null ? (
          <div className="page-header-title-row">
            <h1 className="page-header-title m-0 truncate">{title}</h1>
            {tag}
          </div>
        ) : (
          <h1 className="page-header-title m-0 truncate">{title}</h1>
        )}
        {meta != null && <p className="page-header-meta m-0 truncate">{meta}</p>}
      </div>
      {tabs != null && (
        <div data-slot="page-header-tabs" className="page-header-tabs">
          {tabs}
        </div>
      )}
      {actions != null && (
        <div data-slot="page-header-actions" className="page-header-actions">
          {actions}
        </div>
      )}
    </header>
  );
}
